import dotenv from "dotenv"
import type { UserError } from "fastmcp"
import { FastMCP } from "fastmcp"
// AzureSession shape: { accessToken: string; scopes: string[]; refreshToken?: string; upn?: string }
type OAuthSessionContext = { accessToken?: string }
import {
  decodeBase64Upload,
  describeFetchError,
  filenameFromPath,
  MAX_UPLOAD_SIZE,
  resolveUploadContentType,
  sessionUpload,
  SIMPLE_UPLOAD_LIMIT,
  simpleUpload,
} from "@sapientsai/ms-graph-core"
import { type Either, Left, Right } from "functype/either"
import { z } from "zod"

import { getAccessToken, initializeAuth } from "./auth"
import { createAzureAuthProvider } from "./auth/oauth-provider"
import { GRAPH_API_BASE } from "./auth/scopes"
import { withToken } from "./auth/token-context"
import { initializeGraphClient } from "./client/graph-client"
import {
  copyOnenotePage,
  createContact,
  createDraft,
  createEvent,
  createFolder,
  createForwardDraft,
  createOnenoteNotebook,
  createOnenotePage,
  createOnenoteSection,
  createPlannerBucket,
  createPlannerTask,
  createReplyAllDraft,
  createReplyDraft,
  createTodoTask,
  deleteEvent,
  deleteOnenotePage,
  downloadFile,
  findMeetingAvailability,
  getAuthStatusTool,
  getContact,
  getDriveItem,
  getEvent,
  getGroup,
  getMe,
  getMessage,
  getOnenotePageContent,
  getPlannerTask,
  getSite,
  getUploadConfig,
  getUser,
  graphQuery,
  listAccountsTool,
  listCalendarView,
  listChannelMessages,
  listChannels,
  listChatMessages,
  listChats,
  listContacts,
  listDriveItems,
  listEvents,
  listGroupMembers,
  listGroups,
  listMessages,
  listOnenoteNotebooks,
  listOnenotePages,
  listOnenoteSections,
  listPlannerBuckets,
  listPlannerTasks,
  listPlans,
  listSiteDrives,
  listSiteItems,
  listSites,
  listTeams,
  listTodoLists,
  listTodoTasks,
  listUsers,
  readDocument,
  searchContacts,
  searchFiles,
  searchMessages,
  searchSiteFiles,
  sendChannelMessage,
  sendChatMessage,
  sendDraft,
  sendForward,
  sendMessage,
  sendReply,
  sendReplyAll,
  setAccessTokenTool,
  switchAccountTool,
  updateEvent,
  updateOnenotePageContent,
  updatePlannerTask,
  updatePlannerTaskDetails,
  updateTodoTask,
  uploadFile,
  uploadFileFromPath,
} from "./tools"
import type { ToolDefinition } from "./tools/tool-definitions"
import { filterTools, type ToolFilterConfig } from "./tools/tool-registry"
import type { AuthConfig } from "./types"
import { resolveUploadAccessToken } from "./upload/upload-auth"
import { auditToolCall, auditToolError, auditToolResult } from "./utils/audit"

dotenv.config({ quiet: true })

declare const __VERSION__: string
const VERSION = (typeof __VERSION__ !== "undefined" ? __VERSION__ : "0.0.0-dev") as `${number}.${number}.${number}`

const resolveAuthConfig = (): AuthConfig => {
  const mode = process.env.MS365_AUTH_MODE ?? "interactive"
  const tenantId = process.env.MS365_TENANT_ID ?? "common"
  const clientId = process.env.MS365_CLIENT_ID ?? ""

  switch (mode) {
    case "certificate":
      return {
        mode: "certificate",
        tenantId,
        clientId,
        certPath: process.env.MS365_CERT_PATH ?? "",
        certPassword: process.env.MS365_CERT_PASSWORD,
      }
    case "client-secret":
      return {
        mode: "client-secret",
        tenantId,
        clientId,
        clientSecret: process.env.MS365_CLIENT_SECRET ?? "",
      }
    case "client-token":
      return {
        mode: "client-token",
        accessToken: process.env.MS365_ACCESS_TOKEN,
      }
    case "oauth-proxy":
      return {
        mode: "oauth-proxy",
        tenantId,
        clientId,
        clientSecret: process.env.MS365_CLIENT_SECRET ?? "",
        baseUrl: process.env.MS365_OAUTH_BASE_URL ?? "http://localhost:3000",
      }
    default:
      return {
        mode: "interactive",
        tenantId,
        clientId,
        redirectUri: process.env.MS365_REDIRECT_URI,
      }
  }
}

const setupAuth = async () => {
  const config = resolveAuthConfig()
  const result = await initializeAuth(config)

  result.fold(
    (error) => {
      if (config.mode === "client-token" && !config.accessToken) {
        console.error("[Setup] Client token mode: use set_access_token tool to provide a token")
      } else {
        console.error(`[Error] Authentication failed: ${(error as { message: string }).message}`)
        process.exit(1)
      }
    },
    () => console.error(`[Setup] Authentication initialized (${config.mode} mode)`),
  )

  initializeGraphClient({ getAccessToken })
  console.error("[Setup] Graph client initialized")
}

/* eslint-disable functype/prefer-either -- boundary: converts Either to FastMCP's throw-based error signaling */
const unwrapResult = <T>(result: Either<UserError, T>): T =>
  result.fold(
    (e) => {
      throw e
    },
    (v) => v,
  )
/* eslint-enable functype/prefer-either */

const resolveFilterConfig = (transport: "stdio" | "httpStream"): ToolFilterConfig => ({
  presets: process.env.MS365_PRESETS?.split(",").map((s) => s.trim()),
  enabledPattern: process.env.MS365_ENABLED_TOOLS,
  readOnly: process.env.MS365_READ_ONLY === "true",
  orgMode: process.env.MS365_ORG_MODE === "true",
  requireDraft: process.env.MS365_REQUIRE_DRAFT === "true",
  transport,
})

const FETCH_ALL_PAGES_PARAM = z.boolean().optional().describe("Fetch all pages of results (max 50 pages)")

const toolDefinitions: ReadonlyArray<ToolDefinition> = [
  // === Auth Tools ===
  {
    name: "get_auth_status",
    description: "Get current authentication status, mode, and scopes",
    parameters: z.object({}),
    execute: async () => unwrapResult(await getAuthStatusTool()),
    domain: "auth",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_accounts",
    description: "List all registered accounts and show which is the default",
    parameters: z.object({}),
    execute: async () => unwrapResult(await listAccountsTool()),
    domain: "auth",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "switch_account",
    description: "Switch the default account for subsequent tool calls",
    parameters: z.object({
      account_id: z.string().describe("Account ID to set as default"),
    }),
    execute: async (params) => unwrapResult(await switchAccountTool(params)),
    domain: "auth",
    readOnly: false,
  },
  {
    name: "set_access_token",
    description: "Set or update the access token (client-token auth mode only)",
    parameters: z.object({
      access_token: z.string().describe("The access token for Microsoft Graph"),
      expires_on: z.string().optional().describe("Token expiration time in ISO format"),
    }),
    // eslint-disable-next-line @typescript-eslint/require-await -- FastMCP requires async execute
    execute: async (params) => unwrapResult(setAccessTokenTool(params)),
    domain: "auth",
    readOnly: false,
  },

  // === Mail Tools ===
  {
    name: "list_messages",
    description: "List email messages from your inbox",
    parameters: z.object({
      top: z.number().optional().describe("Number of messages to return (default: 25)"),
      filter: z.string().optional().describe("OData filter expression"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listMessages(params)),
    domain: "mail",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_message",
    description: "Get a specific email message with full body content",
    parameters: z.object({
      message_id: z.string().describe("The message ID"),
    }),
    execute: async (params) => unwrapResult(await getMessage(params)),
    domain: "mail",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "send_message",
    description: "Send a new email message",
    parameters: z.object({
      to: z.string().describe("Recipient email address(es), comma-separated for multiple"),
      subject: z.string().describe("Email subject"),
      body: z.string().describe("Email body content"),
      content_type: z.string().optional().describe("Body content type: Text or HTML (default: Text)"),
    }),
    execute: async (params) => unwrapResult(await sendMessage(params)),
    domain: "mail",
    readOnly: false,
    annotations: { destructiveHint: true },
  },
  {
    name: "send_reply",
    description:
      "Reply to the sender of an email and send immediately. Threads into the conversation and quotes the original. Use create_reply_draft to review before sending.",
    parameters: z.object({
      message_id: z.string().describe("The message ID to reply to"),
      comment: z.string().describe("Reply content (added above the quoted original)"),
    }),
    execute: async (params) => unwrapResult(await sendReply(params)),
    domain: "mail",
    readOnly: false,
    annotations: { destructiveHint: true },
  },
  {
    name: "send_reply_all",
    description:
      "Reply to all recipients of an email and send immediately. Threads into the conversation and quotes the original. Use create_reply_all_draft to review before sending.",
    parameters: z.object({
      message_id: z.string().describe("The message ID to reply to"),
      comment: z.string().describe("Reply content (added above the quoted original)"),
    }),
    execute: async (params) => unwrapResult(await sendReplyAll(params)),
    domain: "mail",
    readOnly: false,
    annotations: { destructiveHint: true },
  },
  {
    name: "send_forward",
    description:
      "Forward an email to new recipients and send immediately. Quotes the original. Use create_forward_draft to review before sending.",
    parameters: z.object({
      message_id: z.string().describe("The message ID to forward"),
      to: z.string().describe("Recipient email address(es), comma-separated for multiple"),
      comment: z.string().optional().describe("Optional note added above the quoted original"),
    }),
    execute: async (params) => unwrapResult(await sendForward(params)),
    domain: "mail",
    readOnly: false,
    annotations: { destructiveHint: true },
  },
  {
    name: "create_reply_draft",
    description:
      "Create a reply draft (to the sender) in the Drafts folder. Threads into the conversation with the original quoted underneath. Review, then send with send_draft.",
    parameters: z.object({
      message_id: z.string().describe("The message ID to reply to"),
      comment: z.string().describe("Reply content (added above the quoted original)"),
    }),
    execute: async (params) => unwrapResult(await createReplyDraft(params)),
    domain: "mail",
    readOnly: false,
  },
  {
    name: "create_reply_all_draft",
    description:
      "Create a reply-all draft (to all recipients) in the Drafts folder. Threads into the conversation with the original quoted underneath. Review, then send with send_draft.",
    parameters: z.object({
      message_id: z.string().describe("The message ID to reply to"),
      comment: z.string().describe("Reply content (added above the quoted original)"),
    }),
    execute: async (params) => unwrapResult(await createReplyAllDraft(params)),
    domain: "mail",
    readOnly: false,
  },
  {
    name: "create_forward_draft",
    description:
      "Create a forward draft in the Drafts folder with the original quoted underneath. Review, then send with send_draft.",
    parameters: z.object({
      message_id: z.string().describe("The message ID to forward"),
      to: z.string().describe("Recipient email address(es), comma-separated for multiple"),
      comment: z.string().optional().describe("Optional note added above the quoted original"),
    }),
    execute: async (params) => unwrapResult(await createForwardDraft(params)),
    domain: "mail",
    readOnly: false,
  },
  {
    name: "search_messages",
    description: "Search email messages",
    parameters: z.object({
      query: z.string().describe("Search query string"),
      top: z.number().optional().describe("Number of results to return (default: 25)"),
    }),
    execute: async (params) => unwrapResult(await searchMessages(params)),
    domain: "mail",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "create_draft",
    description: "Create a new email draft in the Drafts folder",
    parameters: z.object({
      to: z.string().describe("Recipient email address(es), comma-separated for multiple"),
      subject: z.string().describe("Email subject"),
      body: z.string().describe("Email body content"),
      content_type: z.string().optional().describe("Body content type: Text or HTML (default: Text)"),
      cc: z.string().optional().describe("CC recipients (comma-separated email addresses)"),
      bcc: z.string().optional().describe("BCC recipients (comma-separated email addresses)"),
    }),
    execute: async (params) => unwrapResult(await createDraft(params)),
    domain: "mail",
    readOnly: false,
  },
  {
    name: "send_draft",
    description: "Send an existing email draft",
    parameters: z.object({
      message_id: z.string().describe("The draft message ID to send"),
    }),
    execute: async (params) => unwrapResult(await sendDraft(params)),
    domain: "mail",
    readOnly: false,
    annotations: { destructiveHint: true },
  },

  // === Calendar Tools ===
  {
    name: "list_events",
    description:
      "List calendar event resources (/me/events). Returns series masters for recurring meetings, NOT individual occurrences. " +
      "For 'what's on my calendar between X and Y' use list_calendar_view instead — it expands recurrences into instances.",
    parameters: z.object({
      top: z.number().optional().describe("Number of events to return (default: 25)"),
      filter: z.string().optional().describe("OData filter expression"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listEvents(params)),
    domain: "calendar",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_calendar_view",
    description:
      "List event instances on the calendar between start_date_time and end_date_time. Expands recurring series into " +
      "individual occurrences (unlike list_events which returns series masters). Use this for 'what's on my calendar this week'.",
    parameters: z.object({
      start_date_time: z.string().describe("Window start (ISO 8601, e.g. 2026-05-22T00:00:00Z)"),
      end_date_time: z.string().describe("Window end (ISO 8601, e.g. 2026-05-29T00:00:00Z)"),
      top: z.number().optional().describe("Max events to return (default: 50)"),
    }),
    execute: async (params) => unwrapResult(await listCalendarView(params)),
    domain: "calendar",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "find_meeting_availability",
    description:
      "Find meeting time suggestions where all participants are free, ranked by confidence. Searches between " +
      "after_date_time and before_date_time for a slot of duration_minutes. Read-only: suggests times only — " +
      "use create_event to book one.",
    parameters: z.object({
      participants: z
        .array(z.string())
        .min(1)
        .describe("Attendee email addresses. The signed-in user is automatically the organizer."),
      after_date_time: z.string().describe("Search window start (ISO 8601, e.g. 2026-06-04T00:00:00Z)"),
      before_date_time: z.string().describe("Search window end (ISO 8601, e.g. 2026-06-06T00:00:00Z)"),
      duration_minutes: z
        .number()
        .int()
        .min(15)
        .max(480)
        .optional()
        .describe("Meeting length in minutes (default: 30)"),
      max_candidates: z.number().int().min(1).max(50).optional().describe("Max slots to return (default: 3)"),
      is_organizer_optional: z
        .boolean()
        .optional()
        .describe("Whether the organizer's attendance is optional (default: false)"),
    }),
    execute: async (params) => unwrapResult(await findMeetingAvailability(params)),
    domain: "calendar",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_event",
    description: "Get detailed information about a calendar event",
    parameters: z.object({
      event_id: z.string().describe("The event ID"),
    }),
    execute: async (params) => unwrapResult(await getEvent(params)),
    domain: "calendar",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "create_event",
    description: "Create a new calendar event",
    parameters: z.object({
      subject: z.string().describe("Event subject/title"),
      start: z.string().describe("Start date/time (ISO format)"),
      end: z.string().describe("End date/time (ISO format)"),
      time_zone: z.string().optional().describe("Time zone (default: UTC)"),
      location: z.string().optional().describe("Event location"),
      body: z.string().optional().describe("Event description"),
      content_type: z.string().optional().describe("Body content type: Text or HTML (default: Text)"),
      attendees: z.string().optional().describe("Comma-separated email addresses of attendees"),
      is_draft: z
        .boolean()
        .optional()
        .describe(
          "Save to your calendar without inviting attendees (any attendees param is ignored). " +
            "Add attendees and send the meeting later from Outlook. Default: false.",
        ),
      online_meeting: z
        .boolean()
        .optional()
        .describe("Add a Teams meeting to the event; joinUrl is returned in the event details (default: false)"),
    }),
    execute: async (params) => unwrapResult(await createEvent(params)),
    domain: "calendar",
    readOnly: false,
  },
  {
    name: "update_event",
    description: "Update an existing calendar event",
    parameters: z.object({
      event_id: z.string().describe("The event ID to update"),
      subject: z.string().optional().describe("New subject"),
      start: z.string().optional().describe("New start date/time (ISO format)"),
      end: z.string().optional().describe("New end date/time (ISO format)"),
      time_zone: z.string().optional().describe("Time zone (default: UTC)"),
      location: z.string().optional().describe("New location"),
      body: z.string().optional().describe("New description"),
      content_type: z.string().optional().describe("Body content type: Text or HTML (default: Text)"),
      attendees: z
        .string()
        .optional()
        .describe("Comma-separated email addresses; replaces the current attendee list when provided"),
      online_meeting: z
        .boolean()
        .optional()
        .describe(
          "Add a Teams meeting to the event. One-way per Graph: once enabled it cannot be turned off or re-provisioned. Default: false.",
        ),
    }),
    execute: async (params) => unwrapResult(await updateEvent(params)),
    domain: "calendar",
    readOnly: false,
  },
  {
    name: "delete_event",
    description: "Delete a calendar event",
    parameters: z.object({
      event_id: z.string().describe("The event ID to delete"),
    }),
    execute: async (params) => unwrapResult(await deleteEvent(params)),
    domain: "calendar",
    readOnly: false,
    annotations: { destructiveHint: true },
  },

  // === Contacts Tools ===
  {
    name: "list_contacts",
    description: "List contacts",
    parameters: z.object({
      top: z.number().optional().describe("Number of contacts to return (default: 25)"),
      filter: z.string().optional().describe("OData filter expression"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listContacts(params)),
    domain: "contacts",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_contact",
    description: "Get detailed contact information",
    parameters: z.object({
      contact_id: z.string().describe("The contact ID"),
    }),
    execute: async (params) => unwrapResult(await getContact(params)),
    domain: "contacts",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "create_contact",
    description: "Create a new contact",
    parameters: z.object({
      given_name: z.string().describe("First name"),
      surname: z.string().optional().describe("Last name"),
      email: z.string().optional().describe("Email address"),
      mobile_phone: z.string().optional().describe("Mobile phone number"),
      company_name: z.string().optional().describe("Company name"),
      job_title: z.string().optional().describe("Job title"),
    }),
    execute: async (params) => unwrapResult(await createContact(params)),
    domain: "contacts",
    readOnly: false,
  },
  {
    name: "search_contacts",
    description: "Search contacts by name or email",
    parameters: z.object({
      query: z.string().describe("Search query"),
      top: z.number().optional().describe("Number of results (default: 25)"),
    }),
    execute: async (params) => unwrapResult(await searchContacts(params)),
    domain: "contacts",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },

  // === Files Tools ===
  {
    name: "list_drive_items",
    description: "List files and folders in OneDrive",
    parameters: z.object({
      folder_id: z.string().optional().describe("Folder ID (omit for root)"),
      folder_path: z.string().optional().describe("Folder path (e.g., 'Documents' or 'Documents/Subfolder')"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listDriveItems(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_drive_item",
    description: "Get file or folder metadata",
    parameters: z.object({
      item_id: z.string().describe("Drive item ID"),
    }),
    execute: async (params) => unwrapResult(await getDriveItem(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "search_files",
    description: "Search OneDrive/SharePoint files",
    parameters: z.object({
      query: z.string().describe("Search query"),
    }),
    execute: async (params) => unwrapResult(await searchFiles(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "download_file",
    description:
      "Get a file's metadata and download URL. Returns content inline for text files under 100KB. For a " +
      "SharePoint file, pass drive_id as well — without it the item is looked up in your own OneDrive and " +
      "will not be found. For readable text from PDF/DOCX/XLSX, use read_document instead; come here when " +
      "extraction fails (scanned PDFs, unsupported types, files over the extraction cap) or you need raw bytes.",
    parameters: z.object({
      item_id: z.string().describe("Drive item ID"),
      drive_id: z
        .string()
        .optional()
        .describe("Drive ID holding the item. Required for SharePoint; omit for your own OneDrive."),
    }),
    execute: async (params) => unwrapResult(await downloadFile(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "read_document",
    description:
      "Download a file from SharePoint or OneDrive and return its readable text content. Supports DOCX, PDF, XLSX, " +
      "and text-based files. Use instead of download_file when you need document contents. Pair with " +
      "search_site_files (SharePoint) or search_files (OneDrive) to get IDs, then pass " +
      "/drives/{driveId}/items/{itemId}/content or /me/drive/items/{id}/content. Text extraction only, no OCR: " +
      "scanned PDFs return no text.",
    parameters: z.object({
      path: z.string().describe("Graph path to the file content endpoint, ending in /content"),
      api_version: z.enum(["v1.0", "beta"]).optional().describe("Graph API version"),
      format: z.string().optional().describe("Optional conversion format (e.g. 'pdf'), for supported types only"),
      max_chars: z
        .number()
        .int()
        .min(1000)
        .max(200000)
        .optional()
        .describe("Max characters to return (1000-200000, default 50000); content beyond is truncated"),
    }),
    execute: async (params) => unwrapResult(await readDocument(params)),
    domain: "rag",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "create_folder",
    description: "Create a new folder in OneDrive",
    parameters: z.object({
      parent_id: z.string().describe("Parent folder ID"),
      name: z.string().describe("Folder name"),
    }),
    execute: async (params) => unwrapResult(await createFolder(params)),
    domain: "files",
    readOnly: false,
  },
  {
    name: "upload_file",
    description:
      "Upload TEXT content (plain text, markdown, CSV, JSON, HTML, XML) to OneDrive inline via this tool call. For binary files (docx, pdf, images, etc.), use get_upload_config (HTTP/SSE) or upload_file_from_path (stdio/local) — never base64-encode binary into this tool's content param. Max ~4 MB text.",
    parameters: z.object({
      path: z
        .string()
        .describe("Destination path in colon-path format (e.g., /me/drive/root:/Documents/file.txt:/content)"),
      content: z.string().describe("Text content to upload (UTF-8)"),
      content_type: z
        .string()
        .optional()
        .describe(
          "MIME type, default text/plain. Must be a text type (text/*, application/json, application/xml, application/javascript, *+json, *+xml). Binary types are rejected.",
        ),
      conflict_behavior: z
        .enum(["rename", "replace", "fail"])
        .optional()
        .describe('Conflict behavior: "rename" (default), "replace" overwrites, "fail" returns 409 on collision'),
    }),
    execute: async (params) => unwrapResult(await uploadFile(params)),
    domain: "files",
    readOnly: false,
  },
  {
    name: "get_upload_config",
    description:
      "Get an authenticated upload URL + curl command for uploading files to OneDrive. Primary path for binary files or anything >1 MB. Pipe base64 file contents to the returned URL via POST; the server decodes and streams to Microsoft Graph (chunked session upload for >4 MB, up to 250 MB). Intermediate folders are auto-created. Note: response includes operational caveats in notes — read before executing the curl, especially on failure.",
    parameters: z.object({
      path: z
        .string()
        .describe(
          "Graph API destination path ending with :/content (e.g., /me/drive/root:/Documents/file.docx:/content)",
        ),
      localFile: z
        .string()
        .optional()
        .describe("Local file path to include in the curl example. If omitted, a placeholder is used."),
      contentType: z.string().optional().describe("MIME type override. Auto-detected from file extension if omitted."),
      conflictBehavior: z
        .enum(["rename", "replace", "fail"])
        .optional()
        .describe('Conflict behavior: "rename" (default), "replace" overwrites, "fail" returns an error'),
    }),
    execute: async (params) => unwrapResult(await getUploadConfig(params)),
    domain: "files",
    readOnly: false,
  },
  {
    name: "upload_file_from_path",
    description:
      "Upload a local file to OneDrive by reading it from disk on the server. The file must exist on this machine's filesystem. If you generated the file in a cloud container (e.g., claude.ai), first use Desktop Commander's write_file to save it to the user's local filesystem, then call this tool with that local path. Supports files up to 250 MB (chunked session upload above 4 MB). Intermediate folders are auto-created.",
    parameters: z.object({
      local_path: z.string().describe("Absolute path to the local file to upload"),
      path: z
        .string()
        .describe(
          "Graph API destination path ending with :/content (e.g., /me/drive/root:/Documents/file.docx:/content)",
        ),
      content_type: z.string().optional().describe("MIME type override. Auto-detected from file extension if omitted."),
      conflict_behavior: z
        .enum(["rename", "replace", "fail"])
        .optional()
        .describe('Conflict behavior: "rename" (default), "replace" overwrites, "fail" returns an error'),
    }),
    execute: async (params) => unwrapResult(await uploadFileFromPath(params)),
    domain: "files",
    readOnly: false,
  },

  // === SharePoint Tools ===
  {
    name: "list_sites",
    description: "List SharePoint sites. Without a query, returns followed sites. With a query, searches all sites.",
    parameters: z.object({
      query: z.string().optional().describe("Search query to find sites (omit to list followed sites)"),
    }),
    execute: async (params) => unwrapResult(await listSites(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_site",
    description: "Get SharePoint site details",
    parameters: z.object({
      site_id: z.string().describe("Site ID (e.g., 'contoso.sharepoint.com,siteId,webId')"),
    }),
    execute: async (params) => unwrapResult(await getSite(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_site_drives",
    description: "List document libraries (drives) in a SharePoint site",
    parameters: z.object({
      site_id: z.string().describe("Site ID"),
    }),
    execute: async (params) => unwrapResult(await listSiteDrives(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_site_items",
    description: "List files and folders in a SharePoint site's document library",
    parameters: z.object({
      site_id: z.string().describe("Site ID"),
      drive_id: z.string().optional().describe("Drive ID (omit for default document library)"),
      folder_id: z.string().optional().describe("Folder ID (omit for root)"),
      folder_path: z.string().optional().describe("Folder path (e.g., 'General/Reports')"),
    }),
    execute: async (params) => unwrapResult(await listSiteItems(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "search_site_files",
    description: "Search files within a SharePoint site",
    parameters: z.object({
      site_id: z.string().describe("Site ID"),
      query: z.string().describe("Search query"),
      drive_id: z.string().optional().describe("Drive ID (omit to search default document library)"),
    }),
    execute: async (params) => unwrapResult(await searchSiteFiles(params)),
    domain: "files",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },

  // === Chat Tools ===
  {
    name: "list_chats",
    description:
      "List your Teams chats (1:1, group, and meeting chats). Note: the self-chat (notes to self) is not listed here — use chat_id '48:notes' to send to it directly.",
    parameters: z.object({
      top: z.number().optional().describe("Number of chats to return (default: 25)"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listChats(params)),
    domain: "chats" as const,
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_chat_messages",
    description: "List messages in a Teams chat",
    parameters: z.object({
      chat_id: z.string().describe("Chat ID"),
      top: z.number().optional().describe("Number of messages (default: 25)"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listChatMessages(params)),
    domain: "chats" as const,
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "send_chat_message",
    description:
      "Send a message in a Teams chat. Use chat_id '48:notes' to send to the user's self-chat (notes to self).",
    parameters: z.object({
      chat_id: z.string().describe("Chat ID. Use '48:notes' for the user's self-chat."),
      content: z.string().describe("Message content"),
    }),
    execute: async (params) => unwrapResult(await sendChatMessage(params)),
    domain: "chats" as const,
    readOnly: false,
    annotations: { destructiveHint: true },
  },

  // === Teams Tools ===
  {
    name: "list_teams",
    description: "List teams you are a member of",
    parameters: z.object({
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listTeams(params)),
    domain: "teams",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_channels",
    description: "List channels in a team",
    parameters: z.object({
      team_id: z.string().describe("Team ID"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listChannels(params)),
    domain: "teams",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_channel_messages",
    description: "List recent messages in a channel",
    parameters: z.object({
      team_id: z.string().describe("Team ID"),
      channel_id: z.string().describe("Channel ID"),
      top: z.number().optional().describe("Number of messages (default: 25)"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listChannelMessages(params)),
    domain: "teams",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "send_channel_message",
    description: "Send a message to a Teams channel",
    parameters: z.object({
      team_id: z.string().describe("Team ID"),
      channel_id: z.string().describe("Channel ID"),
      content: z.string().describe("Message content"),
    }),
    execute: async (params) => unwrapResult(await sendChannelMessage(params)),
    domain: "teams",
    readOnly: false,
    annotations: { destructiveHint: true },
  },

  // === Users & Groups Tools ===
  {
    name: "get_me",
    description: "Get the authenticated user's profile",
    parameters: z.object({}),
    execute: async () => unwrapResult(await getMe()),
    domain: "users",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_users",
    description: "List users in the organization",
    parameters: z.object({
      top: z.number().optional().describe("Number of users (default: 25)"),
      filter: z.string().optional().describe("OData filter expression"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listUsers(params)),
    domain: "users",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_user",
    description: "Get a specific user's profile",
    parameters: z.object({
      user_id: z.string().describe("User ID or UPN"),
    }),
    execute: async (params) => unwrapResult(await getUser(params)),
    domain: "users",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_groups",
    description: "List groups in the organization",
    parameters: z.object({
      top: z.number().optional().describe("Number of groups (default: 25)"),
      filter: z.string().optional().describe("OData filter expression"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listGroups(params)),
    domain: "groups",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_group",
    description: "Get detailed group information",
    parameters: z.object({
      group_id: z.string().describe("Group ID"),
    }),
    execute: async (params) => unwrapResult(await getGroup(params)),
    domain: "groups",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_group_members",
    description: "List members of a group",
    parameters: z.object({
      group_id: z.string().describe("Group ID"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listGroupMembers(params)),
    domain: "groups",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },

  // === Planner Tools ===
  {
    name: "list_plans",
    description: "List all Planner plans visible to you, aggregated across your group memberships",
    parameters: z.object({}),
    execute: async () => unwrapResult(await listPlans()),
    domain: "planner",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_planner_tasks",
    description: "List tasks in a Planner plan",
    parameters: z.object({
      plan_id: z.string().describe("Plan ID"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listPlannerTasks(params)),
    domain: "planner",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_planner_task",
    description: "Get detailed Planner task information",
    parameters: z.object({
      task_id: z.string().describe("Task ID"),
    }),
    execute: async (params) => unwrapResult(await getPlannerTask(params)),
    domain: "planner",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_planner_buckets",
    description: "List the buckets (columns) in a Planner plan. Use a bucket ID with create_planner_task.",
    parameters: z.object({
      plan_id: z.string().describe("Plan ID"),
    }),
    execute: async (params) => unwrapResult(await listPlannerBuckets(params)),
    domain: "planner",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "create_planner_bucket",
    description: "Create a new bucket (column) in a Planner plan",
    parameters: z.object({
      plan_id: z.string().describe("Plan ID"),
      name: z.string().describe("Bucket name"),
    }),
    execute: async (params) => unwrapResult(await createPlannerBucket(params)),
    domain: "planner",
    readOnly: false,
  },
  {
    name: "create_planner_task",
    description: "Create a new Planner task",
    parameters: z.object({
      plan_id: z.string().describe("Plan ID"),
      title: z.string().describe("Task title"),
      bucket_id: z.string().optional().describe("Bucket ID"),
      due_date: z.string().optional().describe("Due date (ISO format)"),
      assignments: z.string().optional().describe("Comma-separated user IDs to assign"),
    }),
    execute: async (params) => unwrapResult(await createPlannerTask(params)),
    domain: "planner",
    readOnly: false,
  },
  {
    name: "update_planner_task",
    description:
      "Update a Planner task (title, percent_complete — 100 closes it — due date, priority). The ETag " +
      "is auto-fetched if omitted; pass one only to enforce optimistic concurrency.",
    parameters: z.object({
      task_id: z.string().describe("Task ID"),
      etag: z.string().optional().describe("Task ETag — omit to auto-fetch; pass to enforce concurrency"),
      title: z.string().optional().describe("New title"),
      percent_complete: z.number().optional().describe("Completion percentage (0-100; 100 marks complete)"),
      due_date: z.string().optional().describe("New due date (ISO format)"),
      priority: z.number().optional().describe("Priority (0-10)"),
    }),
    execute: async (params) => unwrapResult(await updatePlannerTask(params)),
    domain: "planner",
    readOnly: false,
  },
  {
    name: "update_planner_task_details",
    description:
      "Update a Planner task's details: description, checklist items, and references (links). The ETag " +
      "is fetched automatically. Add, update, or remove entries — updates merge (omitted fields keep " +
      "their current value); update/remove of a missing item is reported as skipped, not a silent no-op. " +
      "Checklist items are addressed by their GUID (from get_planner_task); references by their URL.",
    parameters: z.object({
      task_id: z.string().describe("Task ID"),
      description: z.string().optional().describe("Task description / notes"),
      preview_type: z
        .enum(["automatic", "noPreview", "checklist", "description", "reference"])
        .optional()
        .describe("What shows on the task card face"),
      add_checklist: z
        .array(z.object({ title: z.string(), isChecked: z.boolean().optional() }))
        .optional()
        .describe("Checklist items to add"),
      update_checklist: z
        .array(z.object({ id: z.string(), title: z.string().optional(), isChecked: z.boolean().optional() }))
        .optional()
        .describe("Checklist items to edit, addressed by GUID (e.g. toggle isChecked)"),
      remove_checklist: z.array(z.string()).optional().describe("Checklist item GUIDs to remove"),
      add_references: z
        .array(z.object({ url: z.string(), alias: z.string().optional() }))
        .optional()
        .describe("Reference links to add"),
      update_references: z
        .array(z.object({ url: z.string(), alias: z.string().optional() }))
        .optional()
        .describe("Reference links to edit, addressed by URL (e.g. rename the alias)"),
      remove_references: z.array(z.string()).optional().describe("Reference URLs to remove"),
    }),
    execute: async (params) => unwrapResult(await updatePlannerTaskDetails(params)),
    domain: "planner",
    readOnly: false,
  },

  // === OneNote Tools ===
  {
    name: "list_onenote_notebooks",
    description: "List OneNote notebooks",
    parameters: z.object({
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listOnenoteNotebooks(params)),
    domain: "onenote",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_onenote_sections",
    description: "List sections in a OneNote notebook",
    parameters: z.object({
      notebook_id: z.string().describe("Notebook ID"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listOnenoteSections(params)),
    domain: "onenote",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_onenote_pages",
    description: "List pages in a OneNote section",
    parameters: z.object({
      section_id: z.string().describe("Section ID"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listOnenotePages(params)),
    domain: "onenote",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "get_onenote_page_content",
    description: "Get OneNote page content as HTML",
    parameters: z.object({
      page_id: z.string().describe("Page ID"),
    }),
    execute: async (params) => unwrapResult(await getOnenotePageContent(params)),
    domain: "onenote",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "create_onenote_page",
    description:
      "Create a OneNote page in a section. Content is HTML — OneNote supports a constrained subset and silently drops unsupported CSS/tags.",
    parameters: z.object({
      section_id: z.string().describe("Section ID to create the page in"),
      title: z.string().describe("Page title"),
      content: z.string().describe("Page body as HTML (e.g. <p>Hello</p>)"),
    }),
    execute: async (params) => unwrapResult(await createOnenotePage(params)),
    domain: "onenote",
    readOnly: false,
  },
  {
    name: "update_onenote_page_content",
    description:
      "Append to or modify a OneNote page's content without rewriting it. Targets an element (default 'body') and applies an action. Content is HTML.",
    parameters: z.object({
      page_id: z.string().describe("Page ID to update"),
      content: z.string().describe("HTML content to apply"),
      action: z.enum(["append", "insert", "prepend", "replace"]).optional().describe("Update action (default: append)"),
      target: z.string().optional().describe("Target element data-id or known name (default: body)"),
      position: z.enum(["before", "after"]).optional().describe("Position relative to target, for insert"),
    }),
    execute: async (params) => unwrapResult(await updateOnenotePageContent(params)),
    domain: "onenote",
    readOnly: false,
  },
  {
    name: "create_onenote_section",
    description: "Create a new section in a OneNote notebook",
    parameters: z.object({
      notebook_id: z.string().describe("Notebook ID to create the section in"),
      display_name: z.string().describe("Section name"),
    }),
    execute: async (params) => unwrapResult(await createOnenoteSection(params)),
    domain: "onenote",
    readOnly: false,
  },
  {
    name: "create_onenote_notebook",
    description: "Create a new OneNote notebook",
    parameters: z.object({
      display_name: z.string().describe("Notebook name"),
    }),
    execute: async (params) => unwrapResult(await createOnenoteNotebook(params)),
    domain: "onenote",
    readOnly: false,
  },
  {
    name: "copy_onenote_page",
    description: "Copy a OneNote page to another section. Runs asynchronously in OneNote.",
    parameters: z.object({
      page_id: z.string().describe("Page ID to copy"),
      section_id: z.string().describe("Destination section ID"),
    }),
    execute: async (params) => unwrapResult(await copyOnenotePage(params)),
    domain: "onenote",
    readOnly: false,
  },
  {
    name: "delete_onenote_page",
    description: "Delete a OneNote page. This cannot be undone via the API.",
    parameters: z.object({
      page_id: z.string().describe("Page ID to delete"),
    }),
    execute: async (params) => unwrapResult(await deleteOnenotePage(params)),
    domain: "onenote",
    readOnly: false,
    annotations: { destructiveHint: true },
  },

  // === To Do Tools ===
  {
    name: "list_todo_lists",
    description: "List Microsoft To Do task lists",
    parameters: z.object({
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listTodoLists(params)),
    domain: "todo",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "list_todo_tasks",
    description: "List tasks in a To Do list",
    parameters: z.object({
      list_id: z.string().describe("To Do list ID"),
      fetch_all_pages: FETCH_ALL_PAGES_PARAM,
    }),
    execute: async (params) => unwrapResult(await listTodoTasks(params)),
    domain: "todo",
    readOnly: true,
    annotations: { readOnlyHint: true },
  },
  {
    name: "create_todo_task",
    description: "Create a new To Do task",
    parameters: z.object({
      list_id: z.string().describe("To Do list ID"),
      title: z.string().describe("Task title"),
      body: z.string().optional().describe("Task body/notes"),
      due_date: z.string().optional().describe("Due date (ISO format)"),
      importance: z.string().optional().describe("Importance: low, normal, or high"),
    }),
    execute: async (params) => unwrapResult(await createTodoTask(params)),
    domain: "todo",
    readOnly: false,
  },
  {
    name: "update_todo_task",
    description: "Update a To Do task",
    parameters: z.object({
      list_id: z.string().describe("To Do list ID"),
      task_id: z.string().describe("Task ID"),
      title: z.string().optional().describe("New title"),
      status: z.string().optional().describe("Status: notStarted, inProgress, completed, waitingOnOthers, deferred"),
      due_date: z.string().optional().describe("New due date (ISO format)"),
      importance: z.string().optional().describe("Importance: low, normal, or high"),
      body: z.string().optional().describe("New body/notes"),
    }),
    execute: async (params) => unwrapResult(await updateTodoTask(params)),
    domain: "todo",
    readOnly: false,
  },

  // === Graph Query (Escape Hatch) ===
  {
    name: "graph_query",
    description: "Execute an arbitrary Microsoft Graph API query. Use this for operations not covered by other tools.",
    parameters: z.object({
      method: z.string().describe("HTTP method: GET, POST, PUT, PATCH, or DELETE"),
      path: z.string().describe("Graph API path (e.g., /me/memberOf)"),
      body: z.string().optional().describe("JSON request body as a string"),
      version: z.string().optional().describe("API version: v1.0 or beta (default: v1.0)"),
      headers: z
        .record(z.string(), z.string())
        .optional()
        .describe('Extra request headers, e.g. { "If-Match": "<etag>" } for concurrency-controlled writes'),
    }),
    execute: async (params) => unwrapResult(await graphQuery(params)),
    domain: "query",
    readOnly: false,
    annotations: { destructiveHint: true, openWorldHint: true },
  },
]

type ExecuteContext = { session?: OAuthSessionContext }

const wrapExecute = (tool: ToolDefinition, oauthMode: boolean): never => {
  const baseFn = tool.execute as (p: Record<string, unknown>) => Promise<string>

  // Layer 1: OAuth token injection (wraps the base function)
  const withOAuth = oauthMode
    ? (params: Record<string, unknown>, context: ExecuteContext) =>
        withToken(context.session?.accessToken, () => baseFn(params))
    : (params: Record<string, unknown>) => baseFn(params)

  // Layer 2: Audit logging (wraps the OAuth-aware function)
  const withAudit = async (params: Record<string, unknown>, context: ExecuteContext) => {
    auditToolCall(tool.name, params)
    const start = Date.now()

    try {
      const result = await withOAuth(params, context)
      auditToolResult(tool.name, true, Date.now() - start)
      return result
    } catch (error) {
      auditToolError(tool.name, error instanceof Error ? error.message : String(error))
      auditToolResult(tool.name, false, Date.now() - start)
      throw error
    }
  }

  return withAudit as never
}

const registerTools = (server: FastMCP, allowedTools: Set<string>, oauthMode: boolean) => {
  const toRegister = toolDefinitions.filter((tool) => allowedTools.has(tool.name))

  toRegister.forEach((tool) => {
    server.addTool({
      name: tool.name,
      description: tool.description,
      parameters: tool.parameters,
      execute: wrapExecute(tool, oauthMode),
      annotations: tool.annotations,
    })
  })

  console.error(
    `[Setup] Tools registered: ${toRegister.length}, skipped: ${toolDefinitions.length - toRegister.length}`,
  )
}

const buildUploadWorkflow = (allowedTools: Set<string>): string => {
  const hasFromPath = allowedTools.has("upload_file_from_path")
  const hasUploadConfig = allowedTools.has("get_upload_config")

  const bullets: string[] = ["- Text content → upload_file (inline text, any transport)"]

  if (hasFromPath) {
    bullets.push(
      "- Binary files from the server's local disk → upload_file_from_path (requires absolute path on this machine)",
      "- Binary files generated in a cloud container (e.g., claude.ai) → first save to the user's local filesystem using Desktop Commander's write_file, then call upload_file_from_path with that local path",
    )
  }

  if (hasUploadConfig) {
    bullets.push(
      "- Binary files from HTTP/SSE deployments → get_upload_config returns an authenticated URL + curl command; execute the curl in a shell to upload without routing bytes through the LLM",
    )
  }

  return `\n\nUpload workflows:\n${bullets.join("\n")}`
}

const buildInstructions = (allowedTools: Set<string>): string => {
  const domains = new Set(toolDefinitions.filter((t) => allowedTools.has(t.name)).map((t) => t.domain))
  const domainDescriptions: Record<string, string> = {
    auth: "Authentication: Check auth status and manage tokens",
    mail: "Mail: List, read, send, reply, search, and draft email messages",
    calendar: "Calendar: List, view, create, update, and delete events",
    contacts: "Contacts: List, view, create, and search contacts",
    files:
      "Files: List, view, search, download OneDrive files; create folders; upload files (see Upload workflows below)",
    chats: "Chats: List Teams chats and messages; send chat messages",
    teams: "Teams: List teams, channels, and messages; send channel messages",
    users: "Users: View profiles and list users",
    groups: "Groups: List groups and group members",
    planner: "Planner: List plans and tasks; create and update tasks",
    onenote: "OneNote: List notebooks, sections, pages; read page content",
    todo: "To Do: List task lists and tasks; create and update tasks",
    query: "Graph Query: Execute arbitrary Microsoft Graph API queries",
  }

  const capabilities = [...domains]
    .map((d) => domainDescriptions[d])
    .filter(Boolean)
    .map((desc) => `- ${desc}`)
    .join("\n")

  const uploadSection = domains.has("files") ? buildUploadWorkflow(allowedTools) : ""

  return `A Microsoft 365 MCP server via Microsoft Graph API.\n\nAvailable capabilities:\n${capabilities}${uploadSection}`
}

type UploadRequestContext = {
  header: (name: string) => string | undefined
  query: (name: string) => string | undefined
  arrayBuffer: () => Promise<ArrayBuffer>
}

const handleUpload = async (
  req: UploadRequestContext,
  oauthMode: boolean,
): Promise<{ status: number; body: unknown }> => {
  const path = req.query("path")
  if (!path) return { status: 400, body: { error: "path query parameter is required" } }
  if (!/:\/content$/i.test(path)) {
    return { status: 400, body: { error: 'path must end with ":/content"' } }
  }

  const apiVersion = req.query("apiVersion") ?? "v1.0"
  const conflictBehavior = req.query("conflictBehavior") ?? "rename"
  const explicitContentType = req.query("contentType")
  const encoding = req.query("encoding")

  const authHeader = req.header("authorization") ?? req.header("Authorization")
  const bearer = authHeader?.replace(/^Bearer\s+/i, "")
  const auth = await resolveUploadAccessToken(oauthMode, bearer)
  if (auth.error || !auth.token) {
    return { status: auth.status ?? 401, body: { error: auth.error ?? "Unauthorized" } }
  }

  const rawBufferResult = await (async (): Promise<Either<string, Buffer>> => {
    try {
      return Right(Buffer.from(await req.arrayBuffer()))
    } catch (error) {
      return Left(`Failed to read request body: ${error instanceof Error ? error.message : String(error)}`)
    }
  })()
  if (rawBufferResult.isLeft()) {
    return { status: 400, body: { error: rawBufferResult.value as string } }
  }
  const rawBuffer = rawBufferResult.value as Buffer
  if (rawBuffer.length === 0) return { status: 400, body: { error: "Empty request body" } }

  const buffer = encoding === "base64" ? decodeBase64Upload(rawBuffer) : rawBuffer
  if (buffer.length === 0) return { status: 400, body: { error: "Invalid base64 content" } }
  if (buffer.length > MAX_UPLOAD_SIZE) {
    return { status: 413, body: { error: `File too large (max ${MAX_UPLOAD_SIZE} bytes)` } }
  }

  const filename = filenameFromPath(path)
  const contentType = resolveUploadContentType(explicitContentType, filename)
  const apiBase = `${GRAPH_API_BASE}/${apiVersion}`

  console.error(
    `[Upload] path=${path} bytes=${buffer.length} contentType=${contentType} mode=${buffer.length <= SIMPLE_UPLOAD_LIMIT ? "simple" : "session"}`,
  )

  const result =
    buffer.length <= SIMPLE_UPLOAD_LIMIT
      ? await simpleUpload(apiBase, path, auth.token, buffer, contentType, conflictBehavior)
      : await sessionUpload(apiBase, path, auth.token, buffer, conflictBehavior)

  if (result.isLeft()) {
    const err = result.value as { message: string; status?: number }
    return { status: err.status ?? 500, body: { error: err.message } }
  }

  return { status: 200, body: result.value }
}

const mountUploadRoute = (server: FastMCP, oauthMode: boolean): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Hono app surface
  const app: any = (server as unknown as { getApp?: () => unknown }).getApp?.()
  if (!app) {
    console.error("[Upload] FastMCP.getApp() unavailable; /upload endpoint not mounted")
    return
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const handler = async (c: any) => {
    try {
      const result = await handleUpload(c.req as UploadRequestContext, oauthMode)
      return c.json(result.body, result.status)
    } catch (err) {
      const { message } = describeFetchError(err)
      console.error("[Upload] unhandled error:", message)
      return c.json({ error: message }, 500)
    }
  }

  app.post("/upload", handler)
  app.put("/upload", handler)
  /* eslint-enable @typescript-eslint/no-explicit-any */
  console.error("[Setup] /upload endpoint mounted (POST, PUT)")
}

// === Server Startup ===
const main = async () => {
  const authConfig = resolveAuthConfig()
  const oauthMode = authConfig.mode === "oauth-proxy"

  const transport: "stdio" | "httpStream" = oauthMode
    ? "httpStream"
    : process.env.TRANSPORT_TYPE === "httpStream"
      ? "httpStream"
      : "stdio"

  const filterConfig = resolveFilterConfig(transport)
  const allowedTools = filterTools(filterConfig)

  if (oauthMode) {
    // OAuth proxy mode: FastMCP handles auth via AzureProvider
    const provider = createAzureAuthProvider({
      baseUrl: (authConfig as { baseUrl: string }).baseUrl,
      clientId: (authConfig as { clientId: string }).clientId,
      clientSecret: (authConfig as { clientSecret: string }).clientSecret,
      tenantId: (authConfig as { tenantId: string }).tenantId,
    })

    const server = new FastMCP({
      name: "microsoft365-mcp-server",
      version: VERSION,
      instructions: buildInstructions(allowedTools),
      auth: provider,
      health: { enabled: true, path: "/ping", message: "ok" },
    } as never)

    // Initialize graph client without credential-based auth (tokens come from session)
    initializeGraphClient({ getAccessToken })

    registerTools(server, allowedTools, true)
    mountUploadRoute(server, true)

    const port = parseInt(process.env.PORT ?? "3000", 10)
    const host = process.env.HOST ?? process.env.FASTMCP_HOST ?? "127.0.0.1"
    await server.start({ transportType: "httpStream", httpStream: { port, host } })
    console.error(`[Server] MS 365 MCP Server v${VERSION} (OAuth proxy) running on ${host}:${port}`)
  } else {
    // Standard mode: credential-based auth
    await setupAuth()

    const server = new FastMCP({
      name: "microsoft365-mcp-server",
      version: VERSION,
      instructions: buildInstructions(allowedTools),
      health: { enabled: true, path: "/ping", message: "ok" },
    })

    registerTools(server, allowedTools, false)

    const transportType = process.env.TRANSPORT_TYPE ?? "stdio"

    if (transportType === "httpStream") {
      mountUploadRoute(server, false)
      const port = parseInt(process.env.PORT ?? "3000", 10)
      const host = process.env.HOST ?? process.env.FASTMCP_HOST ?? "127.0.0.1"
      await server.start({ transportType: "httpStream", httpStream: { port, host } })
      console.error(`[Server] MS 365 MCP Server v${VERSION} running on ${host}:${port}`)
    } else {
      await server.start({ transportType: "stdio" })
      console.error(`[Server] MS 365 MCP Server v${VERSION} running on stdio`)
    }
  }
}

main().catch((error) => {
  console.error("[Fatal]", error)
  process.exit(1)
})
